import { DashboardMetricCard, DashboardMetricGrid } from '@yeshuman/sdk'
import { Activity as ActivityIcon, Moon, Heart, TrendingUp } from 'lucide-react'

import { Activity } from '../hooks/use-activities'

interface ActivityStatsProps {
  activities: Activity[]
}

export function ActivityStats({ activities }: ActivityStatsProps) {
  const today = new Date()
  const startOfWeek = new Date(today)
  startOfWeek.setDate(today.getDate() - today.getDay())
  startOfWeek.setHours(0, 0, 0, 0)

  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

  const activitiesThisWeek = activities.filter((a) => {
    const activityDate = new Date(a.date)
    return activityDate >= startOfWeek
  })

  const activitiesThisMonth = activities.filter((a) => {
    const activityDate = new Date(a.date)
    return activityDate >= startOfMonth
  })

  const sleepActivities = activities.filter((a) => a.type === 'sleep')
  const exerciseActivities = activities.filter((a) => a.type === 'exercise')
  const wellnessActivities = activities.filter((a) => a.type === 'wellness')

  const parseDuration = (durationStr?: string): number => {
    if (!durationStr) return 0
    const match = durationStr.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/)
    if (!match) return 0
    const hours = parseInt(match[1] || '0')
    const minutes = parseInt(match[2] || '0')
    return hours + minutes / 60
  }

  const averageSleepDuration = sleepActivities.length > 0
    ? sleepActivities.reduce((sum, a) => sum + parseDuration(a.duration), 0) / sleepActivities.length
    : 0

  const exerciseTypeCounts: Record<string, number> = {}
  exerciseActivities.forEach((a) => {
    const type = a.exercise_type || 'Unknown'
    exerciseTypeCounts[type] = (exerciseTypeCounts[type] || 0) + 1
  })
  const mostCommonExercise = Object.entries(exerciseTypeCounts).sort((a, b) => b[1] - a[1])[0]?.[0]

  const wellnessTypeCounts: Record<string, number> = {}
  wellnessActivities.forEach((a) => {
    const type = a.wellness_type || 'Unknown'
    wellnessTypeCounts[type] = (wellnessTypeCounts[type] || 0) + 1
  })
  const mostCommonWellness = Object.entries(wellnessTypeCounts).sort((a, b) => b[1] - a[1])[0]?.[0]

  return (
    <DashboardMetricGrid>
      <DashboardMetricCard
        title="Total activities"
        value={activities.length}
        description="All time"
        icon={ActivityIcon}
      />
      <DashboardMetricCard
        title="This week"
        value={activitiesThisWeek.length}
        description={`Since ${startOfWeek.toLocaleDateString()}`}
        icon={TrendingUp}
      />
      <DashboardMetricCard
        title="This month"
        value={activitiesThisMonth.length}
        description={`Since ${startOfMonth.toLocaleDateString()}`}
        icon={TrendingUp}
      />
      {sleepActivities.length > 0 ? (
        <DashboardMetricCard
          title="Avg sleep"
          value={`${averageSleepDuration.toFixed(1)}h`}
          description="Average duration"
          icon={Moon}
        />
      ) : null}
      {mostCommonExercise ? (
        <DashboardMetricCard
          title="Top exercise"
          value={<span className="capitalize">{mostCommonExercise}</span>}
          description={`${exerciseTypeCounts[mostCommonExercise]} times`}
          icon={ActivityIcon}
        />
      ) : null}
      {mostCommonWellness ? (
        <DashboardMetricCard
          title="Top wellness"
          value={<span className="capitalize">{mostCommonWellness}</span>}
          description={`${wellnessTypeCounts[mostCommonWellness]} times`}
          icon={Heart}
        />
      ) : null}
    </DashboardMetricGrid>
  )
}
